import React from 'react';
import { useClientGrowth } from '../hooks';

export interface ClientGrowthChartProps {
  isLoading?: boolean;
  height?: number;
}

const ClientGrowthChart: React.FC<ClientGrowthChartProps> = ({ isLoading, height = 300 }) => {
  const { data: chartData, isLoading: dataLoading } = useClientGrowth();

  if (isLoading || dataLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Growth</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Growth</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No client data available
        </div>
      </div>
    );
  }

  const maxTotal = Math.max(...chartData.map(d => d.total), 0);
  const chartHeight = height - 100;
  const chartWidth = 1000;
  const padding = 40;

    const points = chartData.map((item, index) => {
      const x = (index * (chartWidth / chartData.length)) + (chartWidth / chartData.length / 2);
      const y = chartHeight - (item.total / maxTotal) * chartHeight;
      return { x, y, month: item.month, total: item.total, new: item.new };
    });

  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  const areaPathData = `${pathData} L ${points[points.length - 1].x} ${chartHeight} L ${points[0].x} ${chartHeight} Z`;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Growth</h2>
      <div className="relative" style={{ height: `${height}px` }}>
        <svg width="100%" height={height} viewBox={`0 0 ${chartWidth} ${height}`} className="overflow-visible">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const y = padding + (chartHeight * ratio);
            const value = Math.round(maxTotal - (maxTotal * ratio));
            return (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-500"
                >
                  {value}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path
            d={areaPathData}
            fill="url(#clientGrowthGradient)"
            opacity="0.3"
          />

          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke="#10b981"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#10b981"
                className="hover:r-7 transition-all cursor-pointer"
              >
                <title>{point.month}: {point.total} total ({point.new} new)</title>
              </circle>
            </g>
          ))}

          {/* X-axis labels */}
          {chartData.map((item, index) => {
            const x = (index * (chartWidth / chartData.length)) + (chartWidth / chartData.length / 2);
            return (
              <text
                key={index}
                x={x}
                y={chartHeight + padding - 10}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {item.month}
              </text>
            );
          })}

          <defs>
            <linearGradient id="clientGrowthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-gray-600">Total Clients</span>
        </div>
        <div className="text-gray-400">•</div>
        <div className="text-gray-600">
          Latest: <span className="font-semibold text-gray-900">{points[points.length - 1]?.total || 0}</span> clients
        </div>
      </div>
    </div>
  );
};

export default ClientGrowthChart;

